import { msg } from "@lingui/core/macro";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createMemo, Show } from "solid-js";
import { Search } from "@fleet/icons";

import { appRoute } from "./route";
import { AuthPlaceholder } from "@/components/auth-placeholder";
import { PageContainer, PageHeader } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { MailInput } from "@/features/mail/components/field";
import { MessageList } from "@/features/mail/components/message-list";
import { useMailAccounts, useMailSearch } from "@/features/mail/hooks";
import { type MailSearchQuery, validateMailSearchQuery } from "@/features/mail/route-search";

function MailSearchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/mail/search" }) as () => MailSearchQuery;

  const accounts = useMailAccounts();
  // P23 / CC-60 · I2: `.data` only on `isSuccess` (the only field of the
  // solid-query proxy that suspends).
  const accountList = createMemo(() => (accounts.isSuccess ? (accounts.data ?? []) : []));
  const accountId = createMemo(() => {
    const list = accountList();
    const req = search().acct;
    if (req && list.some((a) => a.id === req)) {
      return req;
    }
    return list[0]?.id;
  });

  const query = createMemo(() => search().q ?? "");
  const results = useMailSearch(
    () => accountId(),
    () => query(),
  );

  const setQuery = (q: string) => {
    void navigate({
      to: "/mail/search",
      search: (prev) => ({ ...prev, q: q.trim() ? q : undefined }),
      replace: true,
    });
  };

  return (
    <AuthPlaceholder
      title={t(msg`Search mail`)}
      description={t(msg`Sign in to search across your cached mailboxes offline.`)}
    >
      <PageContainer maxWidth="3xl">
        <PageHeader title={t(msg`Search mail`)} />
        <div class="relative mb-4">
          <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
          <MailInput
            class="pl-9"
            autofocus
            value={query()}
            onInput={(e) => setQuery(e.currentTarget.value)}
            placeholder={t(msg`Search subject, sender, body…`)}
            data-testid="mail-search-input"
          />
        </div>
        <Show
          when={query().trim().length > 0}
          fallback={
            <p class="text-sm text-muted-foreground">
              {t(msg`Type to search your offline mail cache.`)}
            </p>
          }
        >
          <div class="rounded-lg border border-border/40" data-testid="mail-search-results">
            <MessageList
              messages={results.isSuccess ? results.data : undefined}
              loading={results.isLoading}
              error={results.isError}
              onRetry={() => void results.refetch()}
              activeUid={undefined}
              onSelect={(m) =>
                navigate({
                  to: "/mail",
                  search: { acct: accountId(), folder: m.folder, uid: m.uid },
                })
              }
              onToggleFlag={() => {
                /* read-only in search results */
              }}
              emptyHint={t(msg`No messages match your search.`)}
            />
          </div>
        </Show>
      </PageContainer>
    </AuthPlaceholder>
  );
}

export const mailSearchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail/search",
  validateSearch: validateMailSearchQuery,
  component: MailSearchPage,
});
